import { useCallback, useEffect, useRef, useState } from 'react';

// The check itself lives in `src/shared` since spec 125; re-exported here so
// this feature's public surface is unchanged.
import { isTextEntry } from '@/shared/utils/isTextEntry';
export { isTextEntry };

/** Where the next move lands, stopping at both ends rather than wrapping (FR-003). */
export function nextIndex(
  current: number | null,
  delta: number,
  count: number,
): number | null {
  if (count === 0) return null;
  // Nothing focused yet: `j` starts at the top, `k` at the bottom (FR-002).
  if (current === null) return delta > 0 ? 0 : count - 1;
  const moved = current + delta;
  if (moved < 0 || moved >= count) return current;
  return moved;
}

export interface ListNavigationOptions {
  /** How many rows the list holds *after* filtering. */
  count: number;
  /** Brings a row into view; the list draws only what is near the viewport. */
  scrollToIndex: (index: number) => void;
  /**
   * True while something else holds the keyboard — the command palette
   * (FR-007), or a task's drawer over the list (spec 111 FR-006).
   */
  suspended: boolean;
  /** The list's scroll container — where the rows are looked up. */
  containerRef: React.RefObject<HTMLElement | null>;
}

export interface ListNavigation {
  /** Put on each row's wrapper so the hook can find it once it is drawn. */
  rowIndexAttribute: (index: number) => Record<string, number>;
}

/**
 * `j` and `k` move through the task list (spec 081).
 *
 * **Scoped to the list, not bound on `window`** (FR-008). The shortcuts contract asks every
 * new binding to say where it belongs and why: undo is about a document and is therefore
 * global, but "next row" is meaningless away from the rows, and a global `j` would fight
 * every other surface for a letter.
 *
 * **Focus is moved, nothing else** (FR-004). The rows are already links, so the focus ring,
 * Enter-to-open and the screen-reader announcement all exist; this adds only the movement
 * between them. That is why activation is deliberately absent here.
 *
 * **The virtualised list is the hard part, not the key handling.** Only rows near the
 * viewport exist in the DOM, so "focus the next task" can mean scrolling first and focusing
 * after the row has been drawn. The index is therefore state, and the focusing happens in
 * an effect once React has rendered the row that index refers to — a synchronous
 * `querySelector` straight after the key would find nothing for any row below the fold.
 */
export function useListNavigation({
  count,
  scrollToIndex,
  suspended,
  containerRef,
}: ListNavigationOptions): ListNavigation {
  const [rawIndex, setIndex] = useState<number | null>(null);
  /*
    Derived, never reset from an effect (FR-012).

    A filter can remove the row under the cursor, leaving an index pointing past the end of
    the list. Clearing that from a `useEffect` is the obvious move and is wrong twice: it
    is a cascading render, and `react-hooks/set-state-in-effect` rejects it outright.
    Treating an out-of-range index as "nothing focused" needs no effect and cannot get out
    of step with the count it is derived from.
  */
  const index = rawIndex !== null && rawIndex < count ? rawIndex : null;
  /** Set only by a keypress, so an ordinary click never yanks focus. */
  const shouldFocus = useRef(false);

  /*
    Listening on the document, but owned by this list.

    FR-008 asks the binding to be scoped to the surface rather than bound globally, and
    this is what that means in practice: the listener is installed and torn down with the
    list, so it cannot fire on a page that has no rows. Attaching it to the scroll
    container instead would read as more tightly scoped and would not work — the container
    is a plain div, so before the reader has focused a row the keystroke goes to `body` and
    never reaches it, and FR-002 ("nothing focused, `j` starts at the top") could not hold.
  */
  const handleKey = useCallback(
    (event: KeyboardEvent) => {
      if (event.key !== 'j' && event.key !== 'k') return;
      // A modifier means the key belongs to the browser or the OS (FR-006).
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (suspended) return;
      if (isTextEntry(event.target)) return;

      const moved = nextIndex(index, event.key === 'j' ? 1 : -1, count);
      if (moved === null || moved === index) return;

      event.preventDefault();
      shouldFocus.current = true;
      setIndex(moved);
      scrollToIndex(moved);
    },
    [count, index, suspended, scrollToIndex],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
    };
  }, [handleKey]);

  useEffect(() => {
    if (!shouldFocus.current || index === null) return;
    const container = containerRef.current;
    if (!container) return;
    // The row may have been drawn only on this render, which is why this is an effect.
    const row = container.querySelector<HTMLElement>(
      `[data-row-index="${String(index)}"] a`,
    );
    if (!row) return;
    shouldFocus.current = false;
    row.focus();
  });

  return {
    rowIndexAttribute: (rowIndex) => ({ 'data-row-index': rowIndex }),
  };
}
